import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, orderBy, limit, startAfter, getDocs, doc, getDoc, where, getCountFromServer } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functionsEurope } from '../../firebase';
import { notifySuccess, notifyError } from '../../utils/notify';
import { MagnifyingGlassIcon, GiftIcon, XMarkIcon } from '@heroicons/react/24/outline';

const PAGE_SIZE = 20;

function parseDate(val) {
  if (!val) return null;
  if (typeof val?.toDate === 'function') return val.toDate();
  if (typeof val?.seconds === 'number') return new Date(val.seconds * 1000);
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(d) {
  if (!d) return '\u2014';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}


export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');
  const [workspaceNames, setWorkspaceNames] = useState({});
  const [campaignCounts, setCampaignCounts] = useState({}); // companyId -> count
  const [grantTarget, setGrantTarget] = useState(null); // { uid, companyId }
  const [grantAmount, setGrantAmount] = useState(1);
  const [granting, setGranting] = useState(false);

  const loadUsers = useCallback(async (afterDoc = null) => {
    try {
      const constraints = [orderBy('createdAt', 'desc'), limit(PAGE_SIZE)];
      if (afterDoc) constraints.push(startAfter(afterDoc));
      const q = query(collection(db, 'users'), ...constraints);
      const snap = await getDocs(q);
      const newUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      setHasMore(snap.docs.length === PAGE_SIZE);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      if (afterDoc) {
        setUsers(prev => [...prev, ...newUsers]);
      } else {
        setUsers(newUsers);
      }

      // Resolve workspace names and campaign counts in parallel
      const companyIds = [...new Set(newUsers.map(u => u.companyId).filter(Boolean))];
      fetchWorkspaceNames(companyIds);
      fetchCampaignCounts(companyIds);
    } catch (error) {
      console.error('Error loading users:', error);
      notifyError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWorkspaceNames = async (companyIds) => {
    const results = {};
    for (let i = 0; i < companyIds.length; i += 10) {
      const batch = companyIds.slice(i, i + 10);
      await Promise.all(batch.map(async (id) => {
        try {
          const wsDoc = await getDoc(doc(db, 'workspace', id));
          results[id] = wsDoc.exists() ? (wsDoc.data().companyName || wsDoc.data().name || id) : id;
        } catch { results[id] = id; }
      }));
    }
    setWorkspaceNames(prev => ({ ...prev, ...results }));
  };

  const fetchCampaignCounts = async (companyIds) => {
    // count() aggregate is 1 read regardless of result size. The old
    // getDocs() variant fetched every campaign doc per workspace just to
    // call .size — at 100 campaigns/workspace × 20 visible users that
    // was 2,000 reads per admin page load.
    const results = {};
    await Promise.all(companyIds.map(async (cid) => {
      try {
        const q = query(collection(db, 'campaign'), where('companyId', '==', cid));
        const snap = await getCountFromServer(q);
        results[cid] = snap.data().count;
      } catch { results[cid] = 0; }
    }));
    setCampaignCounts(prev => ({ ...prev, ...results }));
  };

  useEffect(() => { loadUsers(null); }, [loadUsers]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(u => {
      const email = (u.email || '').toLowerCase();
      const name = (`${u.firstName || ''} ${u.lastName || ''}`).toLowerCase();
      return email.includes(q) || name.includes(q);
    });
  }, [users, search]);

  const handleGrant = async () => {
    if (!grantTarget?.companyId) {
      notifyError('User has no workspace');
      return;
    }
    setGranting(true);
    try {
      await httpsCallable(functionsEurope, 'adminGrantCredits')({
        workspaceId: grantTarget.companyId,
        credits: grantAmount,
      });
      notifySuccess(`Granted ${grantAmount} credit${grantAmount > 1 ? 's' : ''}`);
      setGrantTarget(null);
      setGrantAmount(1);
    } catch (error) {
      notifyError('Failed to grant credits');
    } finally {
      setGranting(false);
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-12 animate-pulse bg-white/5 rounded-[var(--admin-card-radius)]" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 gch-text-muted pointer-events-none" />
        <input
          type="text"
          placeholder="Search by email or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 rounded-[var(--admin-card-radius)] gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted text-sm focus:ring-2 focus:ring-[var(--admin-accent)] focus:outline-none"
        />
      </div>

      <p className="text-xs gch-text-muted">{filtered.length} user{filtered.length !== 1 ? 's' : ''}{search ? ' matching' : ''}</p>

      {/* Table header — desktop */}
      <div className="hidden lg:grid grid-cols-[2fr_1fr_56px_56px_80px_40px] gap-3 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider gch-text-muted select-none">
        <span>Email</span>
        <span>Workspace</span>
        <span>Plan</span>
        <span className="text-right">Games</span>
        <span>Signed up</span>
        <span />
      </div>

      {/* Rows */}
      <div className="divide-y divide-[var(--admin-border)]">
        {filtered.map(u => {
          const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
          const created = parseDate(u.createdAt);
          const wsName = workspaceNames[u.companyId] || u.companyId || '\u2014';
          const isGrantOpen = grantTarget?.uid === u.id;

          return (
            <div key={u.id}>
              <div className="group grid grid-cols-[1fr_auto] lg:grid-cols-[2fr_1fr_56px_80px_40px] gap-3 items-center px-4 py-2.5 hover:bg-white/[0.03] transition-colors rounded-[var(--admin-card-radius)]">
                {/* Email + name */}
                <div className="min-w-0">
                  <p className="text-sm font-medium gch-text-primary truncate">{u.email || '\u2014'}</p>
                  {name && <p className="text-[10px] gch-text-muted truncate mt-0.5">{name}</p>}
                </div>
                {/* Workspace — desktop */}
                <p className="text-xs gch-text-secondary truncate hidden lg:block" title={wsName}>{wsName}</p>
                {/* Campaign count — desktop */}
                <p className="text-xs gch-text-secondary text-right tabular-nums hidden lg:block">
                  {u.companyId && campaignCounts[u.companyId] != null ? campaignCounts[u.companyId] : '\u2014'}
                </p>
                {/* Date — desktop */}
                <p className="text-[11px] gch-text-muted hidden lg:block">{formatDate(created)}</p>
                {/* Grant credits button */}
                <div className="hidden lg:flex justify-end">
                  <button
                    onClick={() => setGrantTarget(isGrantOpen ? null : { uid: u.id, companyId: u.companyId })}
                    className="p-1.5 rounded-lg hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
                    title="Grant credits"
                  >
                    <GiftIcon className="w-4 h-4 gch-text-secondary" />
                  </button>
                </div>
              </div>

              {/* Grant credits inline form */}
              {isGrantOpen && (
                <div className="px-4 pb-3 pt-1 flex items-center gap-3 ml-4 border-l-2 border-[var(--admin-accent)]/30">
                  <p className="text-xs gch-text-secondary whitespace-nowrap">Credits to grant:</p>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                    className="w-16 px-2 py-1 text-sm rounded-lg gch-input-ghost border border-[var(--admin-border)] gch-text-primary text-center focus:ring-2 focus:ring-[var(--admin-accent)] focus:outline-none"
                  />
                  <button
                    onClick={handleGrant}
                    disabled={granting}
                    className="px-3 py-1 text-xs font-medium rounded-lg bg-[var(--admin-accent)] text-[var(--admin-bg-primary)] hover:brightness-110 transition-all disabled:opacity-50"
                  >
                    {granting ? 'Granting...' : 'Grant'}
                  </button>
                  <button
                    onClick={() => { setGrantTarget(null); setGrantAmount(1); }}
                    className="p-1 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 gch-text-muted" />
                  </button>
                  {!u.companyId && (
                    <p className="text-[10px] text-red-400">No workspace linked</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hasMore && !search && (
        <button
          onClick={() => loadUsers(lastDoc)}
          className="w-full btn-admin-outline text-sm py-2.5 rounded-[var(--admin-card-radius)]"
        >
          Load more
        </button>
      )}

      {filtered.length === 0 && (
        <p className="text-sm gch-text-muted text-center py-8">
          {search ? 'No users match your search.' : 'No users found.'}
        </p>
      )}
    </div>
  );
}
